/**
 * Turn a resolved theme into the CSS text the engine injects.
 *
 * The output is a single stylesheet with one light rule and one dark rule, put
 * into <style id="theme-engine"> at the end of <head>. A stylesheet, not inline
 * styles on <html>: inline styles beat every stylesheet rule, which is exactly
 * how DisplayPreferencesContext currently defeats the .dark.theme-grey block
 * without meaning to. Appending last means the theme wins on source order at
 * equal specificity, and stays overridable by anything more specific.
 */
import { isSafeValue, isSafeFontStack } from './resolve';
import { isValidToken } from './tokenRegistry';
import type { ResolvedTheme, TokenMap } from './types';

/**
 * Light rule. `:root` ties with the `:root` blocks in index.css and wins on
 * source order.
 */
export const LIGHT_SELECTOR = ':root';

/**
 * Dark rule, as a two-selector list.
 *
 * `:root.dark` carries specificity 0,2,0 to match index.css's `.dark.theme-grey`
 * block - a bare `.dark` (0,1,0) would lose to it, and a themed dark mode would
 * silently do nothing for anyone on the Grey variant.
 *
 * `.dark` is then included as well so a NESTED dark container picks the tokens
 * up too. The Customize page renders its dark preview that way, and the semantic
 * defaults in token-semantic.css are already declared on plain `.dark`, so
 * without this the preview would show light token values wearing dark utility
 * classes - a preview that lies about what the theme does.
 */
export const DARK_SELECTOR = ':root.dark, .dark';

/** Weight token name per key, matching token-geometry.css. */
const WEIGHT_VARS = {
  normal: '--fw-normal',
  medium: '--fw-medium',
  semibold: '--fw-semibold',
  bold: '--fw-bold',
} as const;

/** `sem-accent` -> `  --sem-accent: 1 1 1;` for every safe, known token. */
function declarations(tokens: TokenMap): string[] {
  return Object.keys(tokens)
    .sort()                                   // deterministic: same theme, same text
    .filter((name) => isValidToken(name) && isSafeValue(tokens[name]))
    .map((name) => `  --${name}: ${tokens[name].trim()};`);
}

function rule(selector: string, lines: string[]): string {
  if (!lines.length) return '';
  return `${selector} {\n${lines.join('\n')}\n}`;
}

export interface CompileOptions {
  /**
   * Override the rule selectors to confine a theme to part of the page.
   *
   * The Customize page uses this to preview a draft inside its own panel when
   * the user has turned site-wide preview off. Any scope must also appear in the
   * `:root` selector list in token-components.css, or the component tokens stay
   * substituted at :root and the scoped override never reaches them.
   */
  lightSelector?: string;
  darkSelector?: string;
}

/**
 * Compile a resolved theme. Returns '' when the theme is disabled or empty, so
 * the caller can drop the style element entirely rather than leaving a stub.
 */
export function compileTheme(theme: ResolvedTheme, options: CompileOptions = {}): string {
  if (!theme.enabled) return '';

  const light = declarations({ ...theme.tokens.light, ...theme.components.light });
  const dark = declarations({ ...theme.tokens.dark, ...theme.components.dark });

  // Typography and layout are mode-independent, so they belong in the light rule
  // only - emitting them twice would let the dark copy shadow the light one.
  const { fontFamily, fontSizePx, weights } = theme.typography;
  if (fontFamily && isSafeFontStack(fontFamily)) {
    light.push(`  --app-font-family: ${fontFamily.trim()};`);
  }
  if (typeof fontSizePx === 'number' && Number.isFinite(fontSizePx)) {
    light.push(`  --app-font-size: ${fontSizePx}px;`);
  }
  if (weights) {
    for (const key of Object.keys(WEIGHT_VARS).sort() as (keyof typeof WEIGHT_VARS)[]) {
      const value = weights[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        light.push(`  ${WEIGHT_VARS[key]}: ${value};`);
      }
    }
  }

  const { spaceUnitRem, radius } = theme.layout;
  if (typeof spaceUnitRem === 'number' && Number.isFinite(spaceUnitRem)) {
    light.push(`  --space-unit: ${spaceUnitRem}rem;`);
  }
  if (radius) {
    for (const key of Object.keys(radius).sort()) {
      if (isSafeValue(radius[key])) light.push(`  --radius-${key}: ${radius[key].trim()};`);
    }
  }

  return [
    rule(options.lightSelector ?? LIGHT_SELECTOR, light),
    rule(options.darkSelector ?? DARK_SELECTOR, dark),
  ].filter(Boolean).join('\n\n');
}
